<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes, getEmailAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-enrich-from-email',
  () => {
    return api.companies.fetchByEmail({ email: `${segmentation.freeQueries.email}` })
  },
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: `/companies/by-email?email=${segmentation.freeQueries.email}`,
    statuses: [
      {
        code: 200,
        color: 'emerald',
        description: t('statuses.200'),
      },
      {
        code: 404,
        color: 'amber',
        description: t('statuses.404'),
      },
      {
        code: 400,
        color: 'rose',
        description: t('statuses.400'),
      },
    ],
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.email'),
    example: `/companies/by-email?email=${segmentation.freeQueries.email}`,
    name: 'email',
    required: true,
    type: 'string',
  },
  {
    description: t('attributes.simplified'),
    example: `/companies/by-email?email=${segmentation.freeQueries.email}&simplified=true`,
    name: 'simplified',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.refresh'),
    example: `/companies/${segmentation.freeQueries.domain}?refresh=true`,
    name: 'refresh',
    optional: true,
    type: 'boolean',
  },
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.company'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'company',
      type: 'object',
    },
    {
      attributes: getEmailAttributes({ email: segmentation.freeQueries.email }),
      description: t('response.email'),
      name: 'email',
      type: 'object',
    },
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :level-to-open="1"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "email": "The email to enrich.",
      "refresh": "When set to true, triggers a real-time refresh of the company data including website crawling, social media analysis, and AI-powered enrichment. Note: This operation costs 10 additional credits per request.",
      "simplified": "When set to true, returns a simplified version of the company data. The call will be free and no credits will be deducted."
    },
    "response": {
      "company": "The company profile from our database.",
      "email": "Additional information about the email."
    },
    "statuses": {
      "200": "OK",
      "400": "Invalid email",
      "404": "Not found"
    }
  },
  "es": {
    "attributes": {
      "email": "El email a enriquecer.",
      "refresh": "Cuando se establece en true, activa una actualización en tiempo real de los datos de la empresa, incluyendo rastreo del sitio web, análisis de redes sociales y enriquecimiento mediante IA. Nota: esta operación cuesta 10 créditos adicionales por solicitud.",
      "simplified": "Cuando se establece en true, devuelve una versión simplificada de los datos de la empresa. La llamada es gratuita y no se descontarán créditos."
    },
    "response": {
      "company": "El perfil de la empresa desde nuestra base de datos.",
      "email": "Información adicional sobre el email."
    },
    "statuses": {
      "200": "OK",
      "400": "Email inválido",
      "404": "No encontrado"
    }
  },
  "fr": {
    "attributes": {
      "email": "L'email à enrichir.",
      "refresh": "Lorsqu'il est activé (true), déclenche une mise à jour en temps réel des données de l'entreprise, incluant l'exploration du site web, l'analyse des réseaux sociaux et l'enrichissement via l'IA. Remarque : cette opération coûte 10 crédits supplémentaires par requête.",
      "simplified": "Lorsqu'il est activé (true), renvoie une version simplifiée des données de l'entreprise. L'appel est gratuit et aucun crédit ne sera déduit."
    },
    "response": {
      "company": "El perfil de la empresa desde nuestra base de datos.",
      "email": "Información adicional sobre el email."
    },
    "statuses": {
      "200": "OK",
      "400": "Email invalide",
      "404": "Non trouvée"
    }
  }
}
</i18n>
